// src/pages/Support.js
// Page Support : contact configurable (WhatsApp/Tel/Email) + message à l'admin
// Maxula

import React, { useState } from 'react';
import { FiMessageCircle, FiMail, FiPhone, FiSend } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { settingsAPI } from '../services/api';
import { useSettings } from '../context/SettingsContext';
import { useLanguage } from '../i18n/LanguageContext';
import config from '../config';
import { waNumber, waLink } from '../utils/whatsapp';

const Support = () => {
  const { settings } = useSettings() || {};
  const { t } = useLanguage();
  const sup = settings?.support || {};
  // Normalisé : 00212... / 06... cassaient le lien wa.me sur iPhone.
  const wa = waNumber(sup.whatsapp) || waNumber(config.SUPPORT_WHATSAPP_CLEAN) || '';

  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);

  const onField = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const send = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) return toast.error(t('support.writeMessage'));
    try {
      setSending(true);
      await settingsAPI.contact(form);
      toast.success(t('support.sent'));
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast.error(t('support.error'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <div className="text-center mb-10">
        <h1 className="text-4xl font-extrabold text-gray-900">{t('support.title')}</h1>
        <p className="text-gray-500 mt-2">{t('support.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
        {wa && (
          <a href={waLink(wa)} target="_blank" rel="noreferrer"
            className="rounded-2xl border border-gray-200 bg-white p-5 hover:shadow-md transition text-center">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2" style={{ background: '#25D36622', color: '#25D366' }}>
              <FiMessageCircle size={22} />
            </div>
            <div className="font-semibold text-gray-900">WhatsApp</div>
            <div className="text-gray-500 text-sm">+{wa}</div>
          </a>
        )}
        {sup.phone && (
          <a href={`tel:${sup.phone}`} className="rounded-2xl border border-gray-200 bg-white p-5 hover:shadow-md transition text-center">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2 bg-blue-50 text-blue-600"><FiPhone size={22} /></div>
            <div className="font-semibold text-gray-900">{t('support.phone')}</div>
            <div className="text-gray-500 text-sm">{sup.phone}</div>
          </a>
        )}
        {sup.email && (
          <a href={`mailto:${sup.email}`} className="rounded-2xl border border-gray-200 bg-white p-5 hover:shadow-md transition text-center">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2 bg-primary-50 text-primary-600"><FiMail size={22} /></div>
            <div className="font-semibold text-gray-900">Email</div>
            <div className="text-gray-500 text-sm">{sup.email}</div>
          </a>
        )}
      </div>

      {(sup.enableContactForm !== false) && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-900 mb-4">{t('support.sendTitle')}</h2>
          <form onSubmit={send} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input name="name" value={form.name} onChange={onField} placeholder={t('support.name')} className="border rounded-lg px-3 py-2" />
              <input name="email" value={form.email} onChange={onField} placeholder={t('support.email')} className="border rounded-lg px-3 py-2" />
              <input name="phone" value={form.phone} onChange={onField} placeholder={t('support.phone')} className="border rounded-lg px-3 py-2" />
            </div>
            <textarea name="message" value={form.message} onChange={onField} rows={4} placeholder={t('support.message')} className="w-full border rounded-lg px-3 py-2" />
            <button type="submit" disabled={sending}
              className="px-5 py-2.5 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 flex items-center gap-2 disabled:opacity-60">
              <FiSend /> {sending ? t('support.sending') : t('support.send')}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default Support;
